import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  FormikSelectComp,
  MoneyInputComp,
  ToolTips,
} from "../../../../components";
import { useGetPaymentMethodsQuery } from "../../../../store/selectableSlice";
import { useFormikContext } from "formik";
import CurrencyInput from "react-currency-input-field";
import { Form } from "react-bootstrap";
import { useEnums } from "../../../../hooks/useEnums";
import { useUserType } from "../../../../hooks/useUserType";
import { convertToTitleCase } from "../../../../utils/helpers";

const formatCurrency = (amount = 0) => {
  // If amount is falsy or invalid, show 0.00 instead of "Variable"
  if (!amount || isNaN(amount) || amount === null || amount === undefined) {
    return "₦0.00";
  }
  return `₦${parseFloat(amount)
    .toFixed(2)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
};

export default function Payment({ amountBreakdown, onSelectionChange, isPublicSignup = false }) {
  const { data: payment_methods = [] } = useGetPaymentMethodsQuery();
  const { values, setFieldValue } = useFormikContext();
  const enums = useEnums();
  const userType = useUserType();
  
  // Use ref to store the callback to prevent infinite loops
  const onSelectionChangeRef = useRef(onSelectionChange);
  
  // Update ref when callback changes
  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);

  // Check if user is staff (case insensitive & supports array/string)
  const isStaff = useMemo(() => {
    if (!userType) return false;
    if (Array.isArray(userType)) {
      return userType.some((type) =>
        type?.toString?.().toLowerCase().includes("staff")
      );
    }
    return userType?.toString?.().toLowerCase().includes("staff");
  }, [userType]);

  // Map payment method names to enum values
  // The backend expects enum values (BANK_TRANSFER, CASH, CHEQUE, PAYSTACK)
  // but the database has friendly names (Transfer, Cash, Cheque, Card, etc.)
  const mapPaymentMethodNameToEnum = (name) => {
    if (!name) return "";
    const nameUpper = name.toUpperCase();
    if (nameUpper.includes("TRANSFER") || nameUpper.includes("BANK"))
      return "BANK_TRANSFER";
    if (nameUpper === "CASH") return "CASH";
    if (nameUpper === "CHEQUE" || nameUpper === "CHECK") return "CHEQUE";
    if (nameUpper === "CARD" || nameUpper === "PAYSTACK") return "PAYSTACK";
    // Default fallback - try to match by name
    return nameUpper;
  };

  const normalizedPaymentOptions = useMemo(() => {
    const rawOptions = enums?.PaymentMethod
      ? enums.PaymentMethod.map((enumValue) => ({
          label: enumValue.label || enumValue.value,
          value: enumValue.value,
        }))
      : payment_methods.map((payment_method) => ({
          label: payment_method.name,
          value: mapPaymentMethodNameToEnum(payment_method.name),
        }));

    const labelOverrides = {
      BANK_TRANSFER: "Bank Transfer",
      CASH: "Cash",
      CHEQUE: "Cheque",
      PAYSTACK: "Card (Paystack)",
    };

    const uniqueOptionsMap = new Map();
    rawOptions.forEach((option) => {
      if (!option?.value) return;
      const normalizedValue = option.value.toUpperCase();
      if (uniqueOptionsMap.has(normalizedValue)) return;
      const formattedLabel =
        labelOverrides[normalizedValue] ||
        convertToTitleCase(
          option.label || normalizedValue.replace(/_/g, " ").toLowerCase()
        );

      uniqueOptionsMap.set(normalizedValue, {
        value: normalizedValue,
        label: formattedLabel,
      });
    });

    let uniqueOptions = Array.from(uniqueOptionsMap.values());

    // For public signup, only allow Paystack (online payment)
    if (isPublicSignup) {
      uniqueOptions = uniqueOptions.filter((option) =>
        option.value === "PAYSTACK"
      );
    } else if (isStaff) {
      // For staff, only allow offline payment methods
      const allowedForStaff = ["BANK_TRANSFER", "CASH", "CHEQUE"];
      uniqueOptions = uniqueOptions.filter((option) =>
        allowedForStaff.includes(option.value)
      );
    }

    // Sort to keep a predictable order (Bank Transfer, Cash, Cheque, Paystack, Others)
    const desiredOrder = ["BANK_TRANSFER", "CASH", "CHEQUE", "PAYSTACK"];
    uniqueOptions.sort((a, b) => {
      const indexA = desiredOrder.indexOf(a.value);
      const indexB = desiredOrder.indexOf(b.value);
      if (indexA === -1 && indexB === -1) {
        return a.label.localeCompare(b.label);
      }
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

    return uniqueOptions;
  }, [enums?.PaymentMethod, payment_methods, isStaff, isPublicSignup]);

  useEffect(() => {
    if (!values?.payment_method) return;
    if (!normalizedPaymentOptions.length) return;
    if (
      !normalizedPaymentOptions.some(
        (option) => String(option.value) === String(values.payment_method)
      )
    ) {
      setFieldValue("payment_method", "");
    }
  }, [normalizedPaymentOptions, setFieldValue, values?.payment_method]);

  const [checkedItems, setCheckedItems] = useState(() =>
    Array.isArray(amountBreakdown) && amountBreakdown.length > 0
      ? amountBreakdown.reduce((acc, item) => {
          acc[item.id] = item.category === "Compulsory";
          return acc;
        }, {})
      : {}
  );

  // Track custom amounts for voluntary fees (fees with null/undefined amounts)
  const [voluntaryAmounts, setVoluntaryAmounts] = useState(() =>
    Array.isArray(amountBreakdown) && amountBreakdown.length > 0
      ? amountBreakdown.reduce((acc, item) => {
          if (!item.amount && item.amount !== 0) {
            acc[item.id] = "";
          }
          return acc;
        }, {})
      : {}
  );

  const [totalAmount, setTotalAmount] = useState(() =>
    Array.isArray(amountBreakdown) && amountBreakdown.length > 0
      ? amountBreakdown.reduce((sum, item) => {
          if (item.category === "Compulsory") {
            return sum + parseFloat(item.amount || 0);
          }
          return sum;
        }, 0)
      : 0
  );
  
  // Update state when amountBreakdown changes (e.g., when fees are loaded)
  useEffect(() => {
    if (Array.isArray(amountBreakdown) && amountBreakdown.length > 0) {
      const newCheckedItems = amountBreakdown.reduce((acc, item) => {
        acc[item.id] = checkedItems[item.id] !== undefined 
          ? checkedItems[item.id] 
          : item.category === "Compulsory";
        return acc;
      }, {});
      setCheckedItems(newCheckedItems);
      
      const newVoluntaryAmounts = amountBreakdown.reduce((acc, item) => {
        if (!item.amount && item.amount !== 0) {
          acc[item.id] = voluntaryAmounts[item.id] || "";
        }
        return acc;
      }, {});
      setVoluntaryAmounts(newVoluntaryAmounts);
    }
  }, [amountBreakdown.length]); // Only trigger when array length changes

  useEffect(() => {
    const newTotalAmount = amountBreakdown.reduce((sum, item) => {
      if (checkedItems[item.id]) {
        // If it's a voluntary fee (no fixed amount), use the custom amount
        if (!item.amount && item.amount !== 0) {
          const customAmount = parseFloat(voluntaryAmounts[item.id] || 0);
          return sum + (isNaN(customAmount) ? 0 : customAmount);
        }
        // Otherwise use the fixed amount
        return sum + parseFloat(item.amount || 0);
      }
      return sum;
    }, 0);
    
    // Only update if total amount actually changed
    setTotalAmount((prevTotal) => {
      if (prevTotal === newTotalAmount) return prevTotal;
      return newTotalAmount;
    });

    // Prepare selected breakdown with amounts
    const selectedBreakdown = amountBreakdown
      .filter((item) => checkedItems[item.id])
      .map((item) => ({
        id: item.id,
        amount:
          !item.amount && item.amount !== 0
            ? parseFloat(voluntaryAmounts[item.id] || 0)
            : parseFloat(item.amount || 0),
      }));

    // Only call onSelectionChange if it's a function (safety check)
    // Use ref to avoid including callback in dependencies
    if (typeof onSelectionChangeRef.current === 'function') {
      onSelectionChangeRef.current(
        selectedBreakdown.map((item) => item.id),
        newTotalAmount,
        selectedBreakdown
      );
    }
  }, [checkedItems, amountBreakdown, voluntaryAmounts]);

  const handleCheckboxChange = (id) => {
    const wasChecked = checkedItems[id];
    setCheckedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
    // Clear voluntary amount when unchecked
    if (wasChecked) {
      const item = amountBreakdown.find((item) => item.id === id);
      if (!item.amount && item.amount !== 0) {
        setVoluntaryAmounts((prev) => ({
          ...prev,
          [id]: "",
        }));
      }
    }
  };

  const handleVoluntaryAmountChange = (id, value) => {
    setVoluntaryAmounts((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const isVoluntaryFee = (item) => {
    return !item.amount && item.amount !== 0;
  };

  return (
    <div className="space-y-4">
      <div className="row row-cols-1 row-cols-md-2 g-4">
        <div className="col">
          <FormikSelectComp
            fieldName={"payment_method"}
            labelText={"Payment Method"}
            labelClassName={"fw-bold"}
            selectText={"Select payment method"}
            options={normalizedPaymentOptions}
          />
        </div>
        <div className="col">
          <MoneyInputComp
            LabelText={"Registration Amount"}
            LabelClassName={"fw-bold"}
            placeholder={formatCurrency(totalAmount)}
            value={totalAmount.toFixed(2)}
            disabled
            name="amount"
          />
        </div>
      </div>

      <div className="border rounded-4 p-3 bg-white2">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="fw-semibold mb-0 text-black1">Fee Breakdown</h6>
          <span className="text-primary1 fw-semibold">
            Total: {formatCurrency(totalAmount)}
          </span>
        </div>
        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
          {amountBreakdown.map((item) => {
            const isChecked = checkedItems[item.id];
            const isCompulsory = item.category === "Compulsory";
            const isVoluntary = isVoluntaryFee(item);
            const customAmount = voluntaryAmounts[item.id] || "";

            return (
              <div key={item.id} className="col">
                <div className="d-flex flex-column gap-2">
                  <label className="d-flex align-items-center gap-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={isChecked}
                      onChange={() => handleCheckboxChange(item.id)}
                      disabled={isCompulsory}
                    />
                    <ToolTips
                      showText={item.category}
                      text={
                        <div className="d-flex flex-column">
                          <small className="fw-semibold text-black1 text-capitalize">
                            {item.name}
                          </small>
                          <small className="text-muted">
                            {isVoluntary
                              ? "Variable Amount"
                              : formatCurrency(item.amount)}
                          </small>
                        </div>
                      }
                    />
                  </label>
                  {isChecked && isVoluntary && (
                    <div className="ms-4 mt-2">
                      <Form.Group>
                        <Form.Label className="fw-semibold small text-black1">
                          Enter Amount <span className="text-danger">*</span>
                        </Form.Label>
                        <CurrencyInput
                          className="form-control py-2"
                          placeholder="₦0.00"
                          prefix="₦"
                          decimalsLimit={2}
                          value={customAmount}
                          onValueChange={(value) =>
                            handleVoluntaryAmountChange(item.id, value)
                          }
                          allowDecimals
                          allowNegativeValue={false}
                        />
                        {isChecked && isVoluntary && !customAmount && (
                          <Form.Text className="text-danger small">
                            Please enter an amount
                          </Form.Text>
                        )}
                      </Form.Group>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
